import React, {
  useState,
  useMemo,
  useCallback,
  type ReactElement,
} from 'react';

import { Banner, Button, Select } from '@pushwoosh/dumb-components';
import { Horizontal } from '@pushwoosh/kit-helpers';

import { Lang } from '~/src/polyglot';
import { AccountsSkeleton } from '../AccountsSkeleton';
import { ListBox } from './styles';
import type { SelectComponentProps } from '../../SelectAccount.types';
import type { SelectAccountOption } from './AccountsSelect.types';

export function AccountsSelect(props: SelectComponentProps): ReactElement {
  const {
    accountsList,
    errorMessage,
    isLoading,
    isSubmitting,
    onAccountClick,
  } = props;

  const [selectedOption, setSelectedOption] = useState<SelectAccountOption | null>(null);

  const onSubmit = useCallback((): void => {
    if (selectedOption) {
      onAccountClick(selectedOption.value, selectedOption.methods, selectedOption.method);
    }
  }, [selectedOption, onAccountClick]);

  const defaultValue = useMemo(() => {
    if (accountsList[0]) {
      const initialOption = {
        label: accountsList[0].name,
        value: accountsList[0].uid,
        isDisabled: !accountsList[0].allowSelect,
        isAdmin: false,
        methods: accountsList[0].methods,
        method: accountsList[0].method,
      };
      setSelectedOption(initialOption);
      return initialOption;
    }

    return null;
  }, [accountsList]);

  const accountOptions = accountsList.map(({
    uid,
    name,
    allowSelect,
    methods,
    method,
  }) => ({
    label: name,
    value: uid,
    isDisabled: !allowSelect,
    isAdmin: false,
    methods,
    method,
  }));

  return (
    <>
      <ListBox>
        {isLoading && <AccountsSkeleton />}
        <Select
          isLoading={isLoading}
          defaultValue={defaultValue}
          options={accountOptions}
          onChange={(option): void => setSelectedOption(option)}
        />
      </ListBox>
      {!!errorMessage && <Banner type="error" title={errorMessage} />}
      {(!isLoading) && (
        <Horizontal $alignItems="center">
          <Button
            type="submit"
            color="primary"
            isLoading={!!isSubmitting}
            isDisabled={Boolean(!selectedOption)}
            onClick={onSubmit}
          >
            <Lang message="SELECT_ACCOUNT.BUTTON.CONTINUE" />
          </Button>
        </Horizontal>
      )}
    </>
  );
}
